import { useMemo } from 'react';
import { useWeeklyResults } from '@/hooks/useWeeklyResults';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format, startOfWeek } from 'date-fns';
import {
  Phone,
  Calendar,
  Users,
  FileText,
  Trophy,
  XCircle,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Download,
  Share2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { WinCardGenerator } from '@/components/results/WinCardGenerator';

interface StatCardProps {
  label: string;
  value: number;
  previousValue?: number;
  icon: React.ComponentType<{ className?: string }>;
  trend?: 'up' | 'down' | 'neutral';
}

function StatCard({ label, value, previousValue, icon: Icon, trend }: StatCardProps) {
  const change = previousValue !== undefined ? value - previousValue : null;
  const changePercent = change !== null && previousValue !== 0
    ? Math.round((change / previousValue) * 100)
    : null;

  const displayTrend = trend || (change !== null
    ? (change > 0 ? 'up' : change < 0 ? 'down' : 'neutral')
    : 'neutral');

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-3xl font-bold">{value}</p>
            {change !== null && change !== 0 && (
              <div className="flex items-center gap-1 text-xs">
                {displayTrend === 'up' ? (
                  <TrendingUp className="w-3 h-3 text-emerald-500" />
                ) : displayTrend === 'down' ? (
                  <TrendingDown className="w-3 h-3 text-destructive" />
                ) : null}
                <span className={cn(
                  displayTrend === 'up' && 'text-emerald-500',
                  displayTrend === 'down' && 'text-destructive',
                  displayTrend === 'neutral' && 'text-muted-foreground'
                )}>
                  {change > 0 ? '+' : ''}{change} {changePercent !== null && `(${changePercent > 0 ? '+' : ''}${changePercent}%)`}
                </span>
              </div>
            )}
          </div>
          <div className={cn(
            "p-3 rounded-lg",
            displayTrend === 'up' && "bg-emerald-500/10",
            displayTrend === 'down' && "bg-destructive/10",
            displayTrend === 'neutral' && "bg-muted/10"
          )}>
            <Icon className={cn(
              "w-6 h-6",
              displayTrend === 'up' && "text-emerald-500",
              displayTrend === 'down' && "text-destructive",
              displayTrend === 'neutral' && "text-muted-foreground"
            )} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Results() {
  const { data: reports, isLoading } = useWeeklyResults(2);
  const { activeWorkspaceId } = useWorkspaceScope();

  const currentWeek = useMemo(() => {
    if (!reports || reports.length === 0) return null;
    return reports[0];
  }, [reports]);

  const previousWeek = useMemo(() => {
    if (!reports || reports.length < 2) return null;
    return reports[1];
  }, [reports]);

  const weekStartDate = useMemo(() => {
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <LoadingState message="Loading results..." />
      </div>
    );
  }

  if (!currentWeek) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <EmptyState
          title="No results yet"
          description="Complete actions to see your weekly results"
          action={
            <Button asChild>
              <Link to="/app/today">Complete 1 action</Link>
            </Button>
          }
        />
      </div>
    );
  }

  const currentStats = currentWeek.stats as any;
  const previousStats = previousWeek?.stats as any || {};

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Results</h1>
          <p className="text-muted-foreground mt-1">
            Week of {format(weekStartDate, 'MMM d, yyyy')}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Contacts Done"
          value={currentStats.contacts_done || 0}
          previousValue={previousStats.contacts_done}
          icon={Phone}
        />
        <StatCard
          label="Follow-ups Scheduled"
          value={currentStats.followups_scheduled || 0}
          previousValue={previousStats.followups_scheduled}
          icon={Calendar}
        />
        <StatCard
          label="Meetings Set"
          value={currentStats.meetings_set || 0}
          previousValue={previousStats.meetings_set}
          icon={Users}
        />
        <StatCard
          label="Proposals Sent"
          value={currentStats.proposals_sent || 0}
          previousValue={previousStats.proposals_sent}
          icon={FileText}
        />
        <StatCard
          label="Won"
          value={currentStats.won || 0}
          previousValue={previousStats.won}
          icon={Trophy}
          trend={currentStats.won > (previousStats.won || 0) ? 'up' : 'neutral'}
        />
        <StatCard
          label="Lost"
          value={currentStats.lost || 0}
          previousValue={previousStats.lost}
          icon={XCircle}
          trend={currentStats.lost < (previousStats.lost || 0) ? 'up' : 'neutral'}
        />
        <StatCard
          label="Reactivated"
          value={currentStats.reactivated || 0}
          previousValue={previousStats.reactivated}
          icon={TrendingUp}
        />
      </div>

      {/* Win Card Generator */}
      <WinCardGenerator />

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>This Week Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <p className="text-sm text-muted-foreground">Win Rate</p>
              <p className="text-2xl font-bold">
                {currentStats.won + currentStats.lost > 0
                  ? Math.round((currentStats.won / (currentStats.won + currentStats.lost)) * 100)
                  : 0}%
              </p>
            </div>
            {previousWeek && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Last week</p>
                <p className="text-lg font-semibold">
                  {previousStats.won + previousStats.lost > 0
                    ? Math.round((previousStats.won / (previousStats.won + previousStats.lost)) * 100)
                    : 0}%
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Key Highlights</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {currentStats.contacts_done > 0 && (
                <li>• {currentStats.contacts_done} contact{currentStats.contacts_done !== 1 ? 's' : ''} completed</li>
              )}
              {currentStats.meetings_set > 0 && (
                <li>• {currentStats.meetings_set} meeting{currentStats.meetings_set !== 1 ? 's' : ''} scheduled</li>
              )}
              {currentStats.won > 0 && (
                <li>• {currentStats.won} deal{currentStats.won !== 1 ? 's' : ''} won</li>
              )}
              {currentStats.reactivated > 0 && (
                <li>• {currentStats.reactivated} lead{currentStats.reactivated !== 1 ? 's' : ''} reactivated</li>
              )}
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Card */}
      {previousWeek && (
        <Card>
          <CardHeader>
            <CardTitle>Week-over-Week Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">This Week</p>
                  <p className="text-lg font-semibold">
                    {format(new Date(currentWeek.week_start_date), 'MMM d')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Last Week</p>
                  <p className="text-lg font-semibold">
                    {format(new Date(previousWeek.week_start_date), 'MMM d')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

